import { ReactNode, useEffect } from "react";

import { css } from "@compiled/react";
import { MessageDescriptor } from "@formatjs/intl/src/types";
import { FormattedMessage, useIntl } from "react-intl";

import { Field } from "@atlaskit/form";
import CheckCircleOutlineIcon from "@atlaskit/icon/core/status-success";
import WarningIcon from "@atlaskit/icon/core/status-warning";
import Spinner from "@atlaskit/spinner";
import TextField from "@atlaskit/textfield";
import { G300, N0, N40, N100, R400 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { ErrorType, useSiteNameValidation } from "@/components/signup/hooks";
import { fontSize, isProduction } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";

import { useRecommendedSiteName } from "../../hooks";
import { messages as validationMessages } from "../../messages";
import {
    useSiteNameContext,
    type FormattedMessageProps
} from "../../site-name-context";

type InputSiteNameProps = {
    inputLabel?: ReactNode;
    errorMessages?: Partial<Record<ErrorType, MessageDescriptor>>;
};

const IsValidOrLoading = ({
    isValidating,
    siteNameValue,
    validationMessage
}: {
    isValidating: boolean;
    siteNameValue: string;
    validationMessage?: FormattedMessageProps;
}) => {
    if (siteNameValue === "") {
        return null;
    }

    if (isValidating) {
        return <Spinner size="small" testId="spinner" />;
    }

    if (validationMessage) {
        return (
            <WarningIcon label="warning" color={token("color.icon.warning")} />
        );
    }

    return (
        <CheckCircleOutlineIcon
            label="success"
            color={token("color.icon.success")}
        />
    );
};

export const InputSiteName = ({
    inputLabel,
    errorMessages
}: InputSiteNameProps) => {
    useRecommendedSiteName({ onError: console.error }); // onError catches errors from endpoint

    const {
        siteNameValidState: [, setSiteNameValid],
        siteNameValueState: [siteNameValue, setSiteNameValue],
        isSubmittingState: [isSubmitting],
        validationMessageState: [validationMessage, setValidationMessage]
    } = useSiteNameContext();

    const { isValid, isValidating, error } = useSiteNameValidation(
        siteNameValue,
        setSiteNameValid
    );
    const siteNameAddressSuffix = isProduction()
        ? ".atlassian.net"
        : ".jira-dev.com";

    useEffect(() => {
        switch (error) {
            case ErrorType.NO_ERROR:
                setValidationMessage(undefined);
                break;
            case ErrorType.INVALID_NAME:
                setValidationMessage(
                    errorMessages?.[ErrorType.INVALID_NAME] ??
                        validationMessages.invalidName
                );
                break;
            case ErrorType.MAXIMUM_LENGTH:
                setValidationMessage(
                    errorMessages?.[ErrorType.MAXIMUM_LENGTH] ??
                        validationMessages.maxLength
                );
                break;
            case ErrorType.UNAVAILABLE:
                setValidationMessage(
                    errorMessages?.[ErrorType.UNAVAILABLE] ??
                        validationMessages.unavailable
                );
                break;
            case ErrorType.NETWORK_ERROR:
                setValidationMessage(
                    errorMessages?.[ErrorType.NETWORK_ERROR] ??
                        validationMessages.networkError
                );
                break;
        }
    }, [error, setValidationMessage]);

    const label = inputLabel ?? (
        <FormattedMessage
            id="BxpUiSignup.nameSite.inputSiteNameLabel2"
            defaultMessage="Your site"
        />
    );

    return (
        <div css={fieldWrapperCss}>
            <Field
                aria-required={true}
                isRequired
                isDisabled={isSubmitting}
                name="site-name"
                label={<span css={inputLabelWrapperCss}>{label}</span>}
            >
                {({ fieldProps }) => (
                    <div
                        css={[
                            siteNameValue === "" && inputEmptyCss,
                            isValid && inputValidCss,
                            validationMessage !== undefined && inputInvalidCss
                        ]}
                    >
                        <TextField
                            type="text"
                            autoComplete="on"
                            {...fieldProps}
                            value={siteNameValue}
                            onChange={(e) => {
                                setSiteNameValue(e.currentTarget.value);
                                fieldProps.onChange(e);
                            }}
                            elemAfterInput={
                                <div css={siteNameAfterCss}>
                                    <span css={siteNameAfterLabelCss}>
                                        {siteNameAddressSuffix}
                                    </span>
                                    <IsValidOrLoading
                                        isValidating={isValidating}
                                        siteNameValue={siteNameValue}
                                        validationMessage={validationMessage}
                                    />
                                </div>
                            }
                        />
                        {validationMessage && (
                            <div css={siteNameErrorCss}>
                                <FormattedMessage {...validationMessage} />
                            </div>
                        )}
                    </div>
                )}
            </Field>
        </div>
    );
};

export const InputWorkspaceName = () => {
    const intl = useIntl();

    return (
        <InputSiteName
            inputLabel={intl.formatMessage({
                id: "BxpUiSignup.nameSite.inputWorkspaceNameLabel",
                defaultMessage: "Workspace name"
            })}
            errorMessages={{
                [ErrorType.INVALID_NAME]:
                    validationMessages.invalidWorkspaceName,
                [ErrorType.UNAVAILABLE]: validationMessages.unavailableWorkspace
            }}
        />
    );
};

const inputEmptyCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
    "> div": {
        borderColor: `${N40} !important`
    }
});

const inputInvalidCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
    "> div": {
        borderColor: `${R400} !important`
    }
});

const inputValidCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
    "> div": {
        borderColor: `${G300} !important`
    }
});

const fieldWrapperCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
    ["div[role='presentation'], input"]: {
        backgroundColor: `${wacToken("color.neutral.light0")} !important`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
    ["input"]: {
        flex: 1,
        fontSize: fontSize.md,
        width: "auto",
        fontFamily: wacToken("font.charlie.text")
    },
    "&:first-of-type": {
        marginBlockStart: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to remove atlaskit required asterisk
    "label span:nth-of-type(2)": {
        display: "none"
    }
});

const inputLabelWrapperCss = css({
    display: "block",
    fontWeight: 500,
    color: wacToken("color.primary.black"),
    fontFamily: wacToken("font.charlie.text")
});

const siteNameAfterLabelCss = css({
    backgroundColor: N0,
    fontSize: fontSize.sm,
    paddingInlineEnd: 8,
    whiteSpace: "nowrap"
});

const siteNameAfterCss = css({
    alignItems: "center",
    color: N100,
    display: "flex",
    fontSize: fontSize.md,
    fontWeight: 400,
    justifyContent: "flex-end",
    paddingInlineEnd: 8,
    width: "auto"
});

const siteNameErrorCss = css({
    color: R400,
    fontSize: fontSize.xs,
    marginBlockStart: 6,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    a: {
        color: wacToken("color.primary.blue"),
        textDecoration: "underline"
    }
});
